import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Plus, Search, Check, Clock, AlertTriangle,
  Download, RefreshCw, Euro, Calendar, FileText, Printer,
  ChevronsUpDown, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatDateIT } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuoteAssociative() {
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState([]);
  const [stats, setStats] = useState(null);
  const [soci, setSoci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAnno, setFiltroAnno] = useState(new Date().getFullYear().toString());
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showPagaDialog, setShowPagaDialog] = useState(false);
  const [selectedQuota, setSelectedQuota] = useState(null);
  // Stato separato per persona_id (evita oggetti ciclici)
  const [selectedPersonaId, setSelectedPersonaId] = useState(searchParams.get('persona') || '');
  const [newQuota, setNewQuota] = useState({
    anno: new Date().getFullYear(),
    importo: 30,
    note: '',
    tipo_quota: 'quota'
  });
  const [pagamento, setPagamento] = useState({
    metodo_pagamento: 'contanti',
    data_pagamento: new Date().toISOString().split('T')[0],  // Data odierna come default
    ricevuta_numero: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [socioSearchOpen, setSocioSearchOpen] = useState(false);
  
  // Stato per gestione quota duplicata
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const fetchQuote = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filtroAnno !== 'tutti') params.append('anno', filtroAnno);
      if (filtroStato !== 'tutti') params.append('stato', filtroStato);
      
      const response = await fetch(`${API_URL}/api/soci/quote/lista?limit=500&${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuote(data.quote || []);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Errore caricamento quote:', error);
      toast.error('Errore nel caricamento delle quote');
    }
  }, [filtroAnno, filtroStato]);

  const fetchSoci = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci?stato=attivo&limit=200`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSoci(data.soci || []);
      }
    } catch (error) {
      console.error('Errore caricamento soci:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchQuote(), fetchSoci()]);
      setLoading(false);
    };
    loadData();
  }, [fetchQuote, fetchSoci]);

  const handleCreateQuota = async (forceCreate = false) => {
    if (!selectedPersonaId || !newQuota.importo) {
      toast.error('Seleziona un socio e inserisci l\'importo');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      const payloadStr = JSON.stringify({
        persona_id: selectedPersonaId,
        anno: Number(newQuota.anno),
        importo: Number(newQuota.importo),
        note: newQuota.note || '',
        force_create: !!forceCreate,
        tipo_quota: forceCreate ? (newQuota.tipo_quota || 'integrazione') : 'quota'
      });
      
      console.log('Invio quota:', payloadStr);
      
      const response = await fetch(`${API_URL}/api/soci/quote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: payloadStr
      });

      const data = await response.json();
      
      if (response.ok) {
        const tipoMsg = data.is_integrazione ? 'Integrazione' : 'Quota';
        toast.success(`${tipoMsg} ${data.anno} creata: €${data.importo}`);
        setShowNewDialog(false);
        setShowDuplicateWarning(false);
        setDuplicateInfo(null);
        setSelectedPersonaId('');
        setNewQuota({
          anno: new Date().getFullYear(),
          importo: 30,
          note: '',
          tipo_quota: 'quota'
        });
        fetchQuote();
      } else if (response.status === 409 && data.detail?.error === 'quota_esistente') {
        // Quota già esistente - mostra warning con opzione di forzare
        setDuplicateInfo(data.detail);
        setShowDuplicateWarning(true);
      } else {
        // Altri errori
        const errorMsg = typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Errore nella creazione della quota';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Errore creazione quota:', error);
      toast.error(`Errore di connessione: ${error.message || 'Verifica la connessione internet'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceCreateQuota = async () => {
    await handleCreateQuota(true);
  };

  const handleDeleteQuota = async (quotaId, quota) => {
    const socioNome = quota?.socio_nome || 'questo socio';
    if (!window.confirm(`Sei sicuro di voler eliminare la quota di €${quota?.importo || ''} per ${socioNome}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/quote/${quotaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Quota eliminata con successo');
        fetchQuote();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };


  const handlePaga = async () => {
    if (!selectedQuota) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('metodo_pagamento', pagamento.metodo_pagamento);
      if (pagamento.data_pagamento) params.append('data_pagamento', pagamento.data_pagamento);
      if (pagamento.ricevuta_numero) params.append('ricevuta_numero', pagamento.ricevuta_numero);
      
      const response = await fetch(`${API_URL}/api/soci/quote/${selectedQuota.quota_id}/paga?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.contabilita_aggiornata) {
          toast.success('Pagamento registrato e contabilità aggiornata automaticamente');
        } else {
          toast.success('Pagamento registrato con successo');
        }
        setShowPagaDialog(false);
        setSelectedQuota(null);
        fetchQuote();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatoBadge = (stato) => {
    const config = {
      da_pagare: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      pagata: { color: 'bg-green-100 text-green-800', icon: Check },
      scaduta: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      annullata: { color: 'bg-gray-100 text-gray-800', icon: null }
    };
    return config[stato] || config.da_pagare;
  };

  const anni = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 5; i--) {
    anni.push(i);
  }

  const handleExportRicevutePDF = async () => {
    setExportingPDF(true);
    try {
      const token = localStorage.getItem('token');
      const statoParam = filtroStato === 'tutti' ? 'tutte' : filtroStato;
      const response = await fetch(
        `${API_URL}/api/soci/quote/ricevute-pdf?anno=${filtroAnno}&stato=${statoParam}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ricevute_quote_${filtroAnno}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Ricevute PDF scaricate');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella generazione PDF');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="quote-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            Quote Associative
          </h1>
          <p className="text-gray-500 mt-1">Gestione quote annuali dei soci</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportRicevutePDF}
            disabled={exportingPDF}
            data-testid="export-ricevute-btn"
          >
            {exportingPDF ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Printer className="h-4 w-4 mr-2" />
            )}
            Stampa Ricevute
          </Button>
          <Button onClick={() => setShowNewDialog(true)} data-testid="new-quota-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuova Quota
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.totale_quote}</div>
              <p className="text-sm text-gray-500">Quote Totali</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.pagate}</div>
              <p className="text-sm text-gray-500">Pagate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.da_pagare}</div>
              <p className="text-sm text-gray-500">Da Pagare</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-emerald-600">€{stats.importo_incassato?.toFixed(2)}</div>
              <p className="text-sm text-gray-500">/ €{stats.importo_totale?.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={filtroAnno} onValueChange={setFiltroAnno}>
              <SelectTrigger className="w-[150px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli anni</SelectItem>
                {anni.map(a => (
                  <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="da_pagare">Da Pagare</SelectItem>
                <SelectItem value="pagata">Pagate</SelectItem>
                <SelectItem value="scaduta">Scadute</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchQuote()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : quote.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna quota trovata</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Anno</TableHead>
                  <TableHead>Socio</TableHead>
                  <TableHead>Importo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden md:table-cell">Scadenza</TableHead>
                  <TableHead className="hidden md:table-cell">Pagamento</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quote.map((q) => {
                  const badge = getStatoBadge(q.stato);
                  return (
                    <TableRow key={q.quota_id}>
                      <TableCell className="font-medium">{q.anno}</TableCell>
                      <TableCell>{q.nome_socio}</TableCell>
                      <TableCell className="font-medium">€{q.importo?.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${badge.color}`}>
                          {badge.icon && <badge.icon className="h-3 w-3" />}
                          {q.stato?.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatDateIT(q.data_scadenza)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {q.data_pagamento ? (
                          <span className="text-green-600">{formatDateIT(q.data_pagamento)}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {q.stato === 'da_pagare' && (
                          <Button 
                            size="sm" 
                            onClick={() => { setSelectedQuota(q); setShowPagaDialog(true); }}
                            data-testid={`paga-btn-${q.quota_id}`}
                          >
                            <Euro className="h-4 w-4 mr-1" />
                            Paga
                          </Button>
                        )}
                        {q.stato === 'pagata' && q.ricevuta_numero && (
                          <span className="text-sm text-gray-500 mr-2">Ric. {q.ricevuta_numero}</span>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteQuota(q.quota_id, q)}
                          data-testid={`delete-btn-${q.quota_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Quota Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Quota Associativa</DialogTitle>
            <DialogDescription>
              Crea una nuova quota annuale per un socio
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Socio *</Label>
              <Popover open={socioSearchOpen} onOpenChange={setSocioSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={socioSearchOpen}
                    className="w-full justify-between font-normal"
                    data-testid="select-socio"
                  >
                    {selectedPersonaId
                      ? soci.find(s => s.persona_id === selectedPersonaId)?.nome_completo || 'Socio selezionato'
                      : "Cerca un socio..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cerca per nome, cognome o tessera..." />
                    <CommandList>
                      <CommandEmpty>Nessun socio trovato</CommandEmpty>
                      <CommandGroup>
                        {soci.map(s => (
                          <CommandItem
                            key={s.persona_id}
                            value={`${s.nome_completo} ${s.numero_tessera || ''}`}
                            onSelect={() => {
                              setSelectedPersonaId(s.persona_id);
                              setSocioSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedPersonaId === s.persona_id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{s.nome_completo}</span>
                              <span className="text-xs text-muted-foreground">
                                Tessera: {s.numero_tessera || 'N/D'} • {s.tipo_socio || 'Ordinario'}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Anno *</Label>
                <Select 
                  value={newQuota.anno.toString()} 
                  onValueChange={(v) => setNewQuota({...newQuota, anno: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anni.map(a => (
                      <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newQuota.importo}
                  onChange={(e) => setNewQuota({...newQuota, importo: parseFloat(e.target.value)})}
                  data-testid="input-importo"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                value={newQuota.note}
                onChange={(e) => setNewQuota({...newQuota, note: e.target.value})}
                placeholder="Note opzionali"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Annulla
            </Button>
            <Button onClick={() => handleCreateQuota(false)} disabled={submitting}>
              {submitting ? 'Creazione...' : 'Crea Quota'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paga Dialog */}
      <Dialog open={showPagaDialog} onOpenChange={setShowPagaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Pagamento</DialogTitle>
            <DialogDescription>
              {selectedQuota && `Quota ${selectedQuota.anno} - ${selectedQuota.nome_socio} - €${selectedQuota.importo?.toFixed(2)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Data Effettiva Pagamento</Label>
              <Input
                type="date"
                value={pagamento.data_pagamento}
                onChange={(e) => setPagamento({...pagamento, data_pagamento: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Metodo di Pagamento</Label>
              <Select 
                value={pagamento.metodo_pagamento} 
                onValueChange={(v) => setPagamento({...pagamento, metodo_pagamento: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="bonifico">Bonifico Bancario</SelectItem>
                  <SelectItem value="carta">Carta di Credito</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Numero Ricevuta (opzionale)</Label>
              <Input
                value={pagamento.ricevuta_numero}
                onChange={(e) => setPagamento({...pagamento, ricevuta_numero: e.target.value})}
                placeholder="Es. RIC-2026-001"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPagaDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handlePaga} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? 'Registrazione...' : 'Conferma Pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Avviso Quota Esistente */}
      <Dialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Quota Già Esistente
            </DialogTitle>
            <DialogDescription>
              {duplicateInfo && (
                <span className="block mt-2">
                  Esiste già {duplicateInfo.quote_esistenti === 1 ? 'una quota' : `${duplicateInfo.quote_esistenti} quote`} per l'anno {duplicateInfo.anno}.
                  <br />
                  <strong>Totale già registrato: €{duplicateInfo.totale_esistente?.toFixed(2)}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Cosa vuoi fare?</strong>
              </p>
              <ul className="mt-2 text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li><strong>Integrazione:</strong> Se la quota esistente era un acconto e vuoi aggiungere il saldo</li>
                <li><strong>Annulla:</strong> Se hai sbagliato e non vuoi creare quote duplicate</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo di quota da creare</Label>
              <Select 
                value={newQuota.tipo_quota || 'integrazione'} 
                onValueChange={(v) => setNewQuota({...newQuota, tipo_quota: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="integrazione">Integrazione (consigliato)</SelectItem>
                  <SelectItem value="saldo">Saldo finale</SelectItem>
                  <SelectItem value="acconto">Altro acconto</SelectItem>
                  <SelectItem value="quota">Nuova quota separata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Note per questa integrazione</Label>
              <Input
                value={newQuota.note}
                onChange={(e) => setNewQuota({...newQuota, note: e.target.value})}
                placeholder="Es. Saldo quota 2026, Integrazione volontaria..."
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDuplicateWarning(false);
                setDuplicateInfo(null);
              }}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleForceCreateQuota} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? 'Creazione...' : 'Crea Integrazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
